import { arrayUnion, doc, DocumentSnapshot, getDoc, setDoc } from 'firebase/firestore';

import { UserCareerRoadmap, UserData } from '../../redux/slice/roadmap-ai/roadmap-ai.type';
import logger from '../logger';

import { getFirestoreDatabase } from './configure';
import { DatabaseTable } from './database.types';
import { getIdForFormRecord } from './database.utils';

const database: DatabaseTable = DatabaseTable.AI_ROADMAP_RESPONSES;

function sanitizeFirestore<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj, (_key, value) => (value === undefined ? null : value)));
}

async function pushCareerRoadMapAssessment(
  userId: string,
  value: UserCareerRoadmap,
): Promise<void> {
  const recordId: string = await getIdForFormRecord();

  await setDoc(
    doc(getFirestoreDatabase(), database, recordId),
    {
      userId, // always persist userId
      roadMap: arrayUnion(sanitizeFirestore(value)),
    },
    { merge: true },
  );
}

/**
 * Get all AI roadmap responses for the current user.
 */
export async function getRoadMapAIResponsesValues(): Promise<UserData | undefined> {
  try {
    const recordId: string = await getIdForFormRecord();
    const result: DocumentSnapshot<Partial<UserData>> = await getDoc(
      doc(getFirestoreDatabase(), database, recordId),
    );
    return result.data() as UserData | undefined;
  } catch (e) {
    logger.error('Failed to retrieve user career roadmap values.', e);
    throw e;
  }
}

/**
 * Get roadmap records by userId.
 */
async function findRoadmapAIRecords(recordId: string): Promise<UserData | undefined> {
  try {
    const result: DocumentSnapshot<Partial<UserData>> = await getDoc(
      doc(getFirestoreDatabase(), database, recordId),
    );
    return result.data() as UserData | undefined;
  } catch (e) {
    logger.error('Failed to retrieve career-roadmap-assessment values.', e);
    throw e;
  }
}

/**
 * Insert or update roadmap AI responses for the current user.
 */
export async function upsertRoadmapAiResponses(dto: UserCareerRoadmap): Promise<UserData> {
  logger.debug('Appending user career roadmap entry.');

  try {
    const recordId: string = await getIdForFormRecord();
    let userData = await findRoadmapAIRecords(recordId);

    if (dto) {
      await pushCareerRoadMapAssessment(recordId, dto);
      userData = await findRoadmapAIRecords(recordId);
    }

    if (!userData) {
      // Initialize document with full UserData
      const initial: UserData = {
        userId: recordId,
        roadMap: [sanitizeFirestore(dto)],
      };

      await setDoc(doc(getFirestoreDatabase(), database, recordId), initial, { merge: true });
      userData = initial;
    }

    if (!userData) {
      throw new Error('User career roadmap not found.');
    }

    return userData; // ✅ now returning the saved/updated object
  } catch (e) {
    logger.error('Failed to upsert user career roadmap.', e);
    throw e;
  }
}
